import React, { useState } from "react";
import {
    Box,
    Container,
    Divider,
    Heading,
    Image,
    Input,
    SimpleGrid,
    Text,
    Textarea,
    VStack,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import { EntityAiToolkit } from "../../components/EntityAiToolkit";
import { SocialAutomationSettings } from "./SocialAutomationSettings";
import { SocialConnectCenter } from "./SocialConnectCenter";
import { SocialPublishPanel } from "./SocialPublishPanel";

const AiStudioSmokeTestPage = () => {
    const toast = useToast();

    const pageBg = useColorModeValue("gray.50", "gray.900");
    const cardBg = useColorModeValue("white", "gray.800");
    const cardShadow = useColorModeValue("sm", "dark-lg");

    const borderColor = useColorModeValue("gray.200", "gray.700");
    const mutedColor = useColorModeValue("gray.600", "gray.300");
    const headingColor = useColorModeValue("gray.800", "white");

    const inputBg = useColorModeValue("white", "gray.900");
    const inputTextColor = useColorModeValue("gray.800", "whiteAlpha.900");
    const inputBorderColor = useColorModeValue("gray.200", "gray.600");
    const inputHoverBorderColor = useColorModeValue("teal.400", "teal.300");
    const inputFocusBorderColor = useColorModeValue("teal.500", "teal.300");
    const inputDisabledBg = useColorModeValue("gray.100", "whiteAlpha.100");
    const inputInvalidBorderColor = useColorModeValue("red.400", "red.300");
    const placeholderColor = useColorModeValue("gray.400", "gray.500");

    const emptyImageBg = useColorModeValue("gray.50", "whiteAlpha.100");
    const emptyImageBorderColor = useColorModeValue("gray.300", "gray.600");
    const previewBg = useColorModeValue("white", "gray.800");

    const [form, setForm] = useState({
        title: "HP ZBook Mobile Workstation",
        description:
            "Good condition, suitable for business, design work and school.",
        price: "750000",
        location: "Lilongwe",
        imageUrl: "",
    });

    const entityId = "frontend-smoke-test-product";
    const productUrl = `https://pickdengu.mw/products/${entityId}`;

    const sharedInputStyles = {
        bg: inputBg,
        color: inputTextColor,
        borderColor: inputBorderColor,
        borderRadius: "xl",
        transition: "all 0.18s ease",
        _placeholder: {
            color: placeholderColor,
        },
        _hover: {
            borderColor: inputHoverBorderColor,
        },
        _focus: {
            borderColor: inputFocusBorderColor,
            boxShadow: `0 0 0 1px ${inputFocusBorderColor}`,
        },
        _focusVisible: {
            borderColor: inputFocusBorderColor,
            boxShadow: `0 0 0 1px ${inputFocusBorderColor}`,
        },
        _invalid: {
            borderColor: inputInvalidBorderColor,
            boxShadow: `0 0 0 1px ${inputInvalidBorderColor}`,
        },
        _disabled: {
            bg: inputDisabledBg,
            opacity: 0.75,
            cursor: "not-allowed",
        },
        sx: {
            "&:-webkit-autofill": {
                boxShadow: `0 0 0px 1000px ${inputBg} inset`,
                WebkitTextFillColor: inputTextColor,
                caretColor: inputTextColor,
                transition: "background-color 9999s ease-in-out 0s",
            },
        },
    };

    return (
        <Box minH="100vh" w="100%" bg={pageBg}>
            <Container
                maxW="100%"
                w="100%"
                px={{ base: 4, md: 6, xl: 8 }}
                py={8}
            >
                <VStack align="stretch" spacing={6} w="100%">
                    <Box w="100%">
                        <Heading size="lg" color={headingColor}>
                            PickDengu AI Studio Test
                        </Heading>
                        <Text color={mutedColor} mt={2}>
                            Test AI descriptions, image generation, social connection and
                            logged-in seller account publishing.
                        </Text>
                    </Box>

                    <Divider borderColor={borderColor} />

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} w="100%">
                        <VStack
                            align="stretch"
                            spacing={4}
                            bg={cardBg}
                            borderWidth="1px"
                            borderColor={borderColor}
                            borderRadius="2xl"
                            p={5}
                            boxShadow={cardShadow}
                            w="100%"
                            minW={0}
                        >
                            <Heading size="sm" color={headingColor}>
                                Listing Details
                            </Heading>

                            <Input
                                value={form.title}
                                placeholder="Title"
                                {...sharedInputStyles}
                                onChange={(event) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        title: event.target.value,
                                    }))
                                }
                            />

                            <Textarea
                                value={form.description}
                                placeholder="Description"
                                minH="120px"
                                resize="vertical"
                                {...sharedInputStyles}
                                onChange={(event) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        description: event.target.value,
                                    }))
                                }
                            />

                            <Input
                                value={form.price}
                                placeholder="Price"
                                inputMode="numeric"
                                {...sharedInputStyles}
                                onChange={(event) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        price: event.target.value,
                                    }))
                                }
                            />

                            <Input
                                value={form.location}
                                placeholder="Location"
                                {...sharedInputStyles}
                                onChange={(event) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        location: event.target.value,
                                    }))
                                }
                            />

                            <EntityAiToolkit
                                entityType="product"
                                entityId={entityId}
                                title={form.title}
                                description={form.description}
                                price={Number(form.price || 0)}
                                location={form.location}
                                initialFields={{
                                    condition: "used",
                                    category: "Laptops",
                                    notes: form.description,
                                }}
                                onApplyText={(result: any) => {
                                    setForm((prev) => ({
                                        ...prev,
                                        title:
                                            result.pickDenguTitle ||
                                            result.marketplaceTitle ||
                                            result.title ||
                                            prev.title,
                                        description:
                                            result.fullDescription ||
                                            result.shortDescription ||
                                            result.description ||
                                            prev.description,
                                    }));

                                    toast({
                                        title: "AI text applied",
                                        description: "The generated text now matches this listing.",
                                        status: "success",
                                        duration: 2500,
                                        isClosable: true,
                                    });
                                }}
                                onApplyImage={(asset: any) => {
                                    setForm((prev) => ({
                                        ...prev,
                                        imageUrl: asset.url,
                                    }));

                                    toast({
                                        title: "AI image selected",
                                        description: "The selected image was applied.",
                                        status: "success",
                                        duration: 2500,
                                        isClosable: true,
                                    });
                                }}
                            />
                        </VStack>

                        <Box
                            bg={cardBg}
                            borderWidth="1px"
                            borderColor={borderColor}
                            borderRadius="2xl"
                            p={5}
                            boxShadow={cardShadow}
                            w="100%"
                            minW={0}
                        >
                            <Heading size="sm" mb={3} color={headingColor}>
                                Selected Image
                            </Heading>

                            {form.imageUrl ? (
                                <Box
                                    bg={previewBg}
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                    borderRadius="xl"
                                    overflow="hidden"
                                    w="100%"
                                >
                                    <Image
                                        src={form.imageUrl}
                                        alt={form.title}
                                        objectFit="contain"
                                        w="100%"
                                        maxH="460px"
                                    />
                                </Box>
                            ) : (
                                <Box
                                    borderWidth="1px"
                                    borderStyle="dashed"
                                    borderColor={emptyImageBorderColor}
                                    borderRadius="xl"
                                    minH="300px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    color={mutedColor}
                                    textAlign="center"
                                    px={6}
                                    bg={emptyImageBg}
                                    w="100%"
                                >
                                    No image selected yet. Open AI Tools, upload an image,
                                    generate a version, then apply it here.
                                </Box>
                            )}
                        </Box>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
                        <SocialConnectCenter />
                        <SocialAutomationSettings />
                    </SimpleGrid>

                    <SocialPublishPanel
                        entityType="product"
                        entityId={entityId}
                        title={form.title}
                        description={form.description}
                        price={Number(form.price || 0)}
                        location={form.location}
                        imageUrl={form.imageUrl}
                        productUrl={productUrl}
                    />
                </VStack>
            </Container>
        </Box>
    );
};

export default AiStudioSmokeTestPage;